import {
  MessageCircle,
  PackageCheck,
  Pill,
  Printer,
  ScanBarcode,
  ShieldCheck,
  TriangleAlert,
  Truck,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Tabs,
  type Column,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  type DispensingRefill,
  type DispensingRx,
  useDispensaryActions,
  useDispensingQueue,
} from '@/features/admin-v2/api';
import { useDeliveries } from '@/features/deliveries/api';
import {
  DELIVERY_PERMISSIONS,
  type StaffDelivery,
} from '@/features/deliveries/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { DeliveriesTab } from './DeliveriesTab';
import { DispensedTab } from './DispensedTab';
import { DispensaryAllergies } from './DispensaryAllergies';
import { LabelsDialog } from './LabelsDialog';
import { OutsideRxBadge, OutsideRxDetail } from './OutsidePrescription';
import { OutsideRxConfirmDialog } from './OutsideRxConfirmDialog';
import { PackDeliveryDialog } from './PackDeliveryDialog';
import { SafetyChecksDialog } from './SafetyChecksDialog';
import {
  requiresIdCheck,
  requiresSecondCheck,
  safetyChecksComplete,
} from './safety-checks';
import { useDispenseWithOutsideRx } from './outside-rx';
import styles from './DispensaryPage.module.css';

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

const RX_STATUS: Record<string, string> = {
  pending: 'To dispense',
  dispensed: 'Dispensed',
  out_of_stock: 'Out of stock',
  delivery_packed: 'Packed for delivery',
  delivered: 'Delivered',
};
const rxStatusLabel = (s: string) => RX_STATUS[s] ?? s.replaceAll('_', ' ');

function statusTone(status: string) {
  if (['dispensed', 'delivered'].includes(status)) return 'success' as const;
  if (status === 'out_of_stock') return 'danger' as const;
  if (status === 'delivery_packed') return 'info' as const;
  return 'warning' as const;
}

type DispensaryTab = 'prescriptions' | 'dispensed' | 'deliveries';

export function DispensaryPage() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const canManageDelivery = hasPermission(DELIVERY_PERMISSIONS.manage);
  const queue = useDispensingQueue();
  const deliveries = useDeliveries('open');
  const [tab, setTab] = useState<DispensaryTab>('prescriptions');
  const [packing, setPacking] = useState<StaffDelivery | null>(null);
  const closePacking = useCallback(() => setPacking(null), []);
  const actions = useDispensaryActions();
  const outsideRx = useDispenseWithOutsideRx();
  const toast = useToast();
  const [open, setOpen] = useState<DispensingRx | null>(null);
  // RL-1629: the dialog follows the live prescription (scans tick at once).
  const [safetyFor, setSafetyFor] = useState<string | null>(null);
  const setSafetyRx = (rx: DispensingRx | null) =>
    setSafetyFor(rx?.prescriptionId ?? null);
  const [oos, setOos] = useState<DispensingRx | null>(null);
  const [labelsFor, setLabelsFor] = useState<string | null>(null);
  // RL-1643: a reprint from the Dispensed tab (not in the to-dispense list).
  const [labelsDone, setLabelsDone] = useState<DispensingRx | null>(null);
  const [note, setNote] = useState(
    'Temporarily out of stock. Doctor notified.',
  );

  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const done = (title: string) => toast.show({ tone: 'success', title });

  /** RL-1186: the patient's same-day request still waiting to be packed. */
  const requested = (rx: DispensingRx) =>
    deliveries.data?.find(
      (d) => d.prescriptionId === rx.prescriptionId && d.status === 'requested',
    );
  const safetyRx = safetyFor
    ? (queue.data?.find((rx) => rx.prescriptionId === safetyFor) ?? null)
    : null;
  const labelsRx = labelsFor
    ? (queue.data?.find((rx) => rx.prescriptionId === labelsFor) ?? null)
    : null;
  const packingRx = packing
    ? (queue.data?.find((rx) => rx.prescriptionId === packing.prescriptionId) ??
      null)
    : null;
  const pack = (
    rx: DispensingRx,
    delivery: { address: string; slot: string; courier: string },
  ) =>
    void outsideRx.dispense(rx, { delivery }).then((outcome) => {
      // On `confirm` the outside-prescription dialog takes over.
      if (outcome !== 'failed') setPacking(null);
    });
  const toPack =
    deliveries.data?.filter((d) => d.status === 'requested').length ?? 0;

  if (!branch) {
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose the branch you are dispensing from in the branch switch.
        </p>
      </PageContainer>
    );
  }

  const columns: Column<DispensingRx>[] = [
    {
      key: 'rx',
      header: 'Rx',
      render: (rx) => (
        <>
          <span className="mono">{rx.number}</span>
          <p className={styles.sub}>{when(rx.signedAt)}</p>
          {rx.refill && (
            <p className={styles.sub}>
              {t('dispensary.refillOf', {
                number: rx.refill.originalPrescription.number ?? '-',
              })}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (rx) => (
        <>
          <p className={styles.name}>{rx.patient.fullName}</p>
          <p className={styles.sub}>{rx.patient.identity ?? 'No ID shown'}</p>
        </>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (rx) => rx.items.map((i) => i.medicine).join(', '),
    },
    {
      key: 'signed',
      header: 'Signed by',
      render: (rx) => rx.prescriber.fullName,
    },
    {
      key: 'status',
      header: 'Status',
      render: (rx) => (
        <div className={styles.badges}>
          <Badge tone={statusTone(rx.status)} dot>
            {rxStatusLabel(rx.status)}
          </Badge>
          {rx.source === 'refill' && (
            <Badge tone="info">{t('dispensary.refillBadge')}</Badge>
          )}
          {requiresIdCheck(rx) && (
            <Badge tone={rx.needsIdCheck ? 'warning' : 'success'}>
              {rx.needsIdCheck
                ? t('dispensary.badgeIdDue')
                : t('dispensary.badgeIdDone')}
            </Badge>
          )}
          {requiresSecondCheck(rx) && (
            <Badge tone={rx.needsSecondCheck ? 'warning' : 'success'}>
              {rx.needsSecondCheck
                ? t('dispensary.badgeSecondDue')
                : t('dispensary.badgeSecondDone')}
            </Badge>
          )}
          {!rx.deliverable && <Badge>Counter only</Badge>}
          {requested(rx) && <Badge tone="info">Delivery requested</Badge>}
          <OutsideRxBadge doc={rx.outsidePrescription} />
          <Badge tone={safetyChecksComplete(rx) ? 'success' : 'warning'}>
            {safetyChecksComplete(rx)
              ? t('dispensary.safetyChecksComplete')
              : t('dispensary.safetyChecksDue')}
          </Badge>
        </div>
      ),
    },
    {
      key: 'safety',
      header: t('dispensary.safetyColumn'),
      render: (rx) => (
        <div
          className={styles.safetyControls}
          aria-label={`${rx.number} ${t('dispensary.safetyColumn')}`}
        >
          <Button
            size="sm"
            icon={<ScanBarcode size={14} />}
            onClick={() => setSafetyRx(rx)}
          >
            {t('dispensary.scanControl')}
          </Button>
          <Button
            size="sm"
            icon={<ShieldCheck size={14} />}
            onClick={() => setSafetyRx(rx)}
          >
            {t('dispensary.clinicalControl')}
          </Button>
          <Button
            size="sm"
            icon={<MessageCircle size={14} />}
            onClick={() => setSafetyRx(rx)}
          >
            {t('dispensary.counsellingControl')}
          </Button>
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (rx) => (
        <div className={`${styles.rowActions} ${styles.actionsCell}`}>
          <Button size="sm" onClick={() => setOpen(rx)}>
            Open
          </Button>
          {canManageDelivery && requested(rx) && (
            <Button
              size="sm"
              icon={<Truck size={14} />}
              onClick={() => setPacking(requested(rx) ?? null)}
            >
              Pack for delivery
            </Button>
          )}
          <Button
            size="sm"
            icon={<Printer size={14} />}
            onClick={() => setLabelsFor(rx.prescriptionId)}
          >
            {t('dispensingLabels.open')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<PackageCheck size={14} />}
            disabled={!safetyChecksComplete(rx)}
            title={
              safetyChecksComplete(rx)
                ? undefined
                : t('dispensary.completeChecksFirst')
            }
            onClick={() => void outsideRx.dispense(rx)}
          >
            Dispense
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={<TriangleAlert size={14} />}
            onClick={() => setOos(rx)}
          >
            Out of stock
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card
        title={
          tab === 'deliveries'
            ? 'Same-day deliveries'
            : tab === 'dispensed'
              ? t('dispensary.dispensedTitle')
              : 'Signed prescriptions'
        }
        flush
        actions={
          <span className={styles.sub}>
            {tab === 'deliveries'
              ? 'Requests from the app and packed deliveries'
              : tab === 'dispensed'
                ? t('dispensary.dispensedSub')
                : 'Appear here the second the doctor signs'}
          </span>
        }
      >
        <Tabs
          label="Dispensary"
          items={[
            { value: 'prescriptions', label: 'Prescriptions' },
            { value: 'dispensed', label: t('dispensary.dispensedTab') },
            { value: 'deliveries', label: 'Deliveries', count: toPack },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'dispensed' ? (
          <DispensedTab onOpen={setOpen} onLabels={setLabelsDone} />
        ) : tab === 'deliveries' ? (
          <DeliveriesTab
            branch={branch}
            prescriptions={queue.data}
            canManage={canManageDelivery}
            onPack={setPacking}
          />
        ) : (
          <DataTable
            caption="Prescriptions to dispense"
            columns={columns}
            rows={queue.data}
            rowKey={(rx) => rx.prescriptionId}
            loading={queue.isLoading}
            error={queue.error}
            onRetry={() => void queue.refetch()}
            empty={
              <EmptyState
                icon={<Pill size={22} />}
                title="No signed prescriptions"
                body="Signed prescriptions appear here when a doctor signs them."
              />
            }
          />
        )}
      </Card>
      <p className={styles.warnNote}>
        Controlled items (Group B poisons) are counter-only: confirm MyKad at
        hand-over. Same-day delivery is offered for other items within the{' '}
        {branch.name} area. Dispensing at the counter cancels a requested
        delivery (the patient is told; an unpaid delivery fee is voided).
      </p>
      <PrescriptionDrawer rx={open} onClose={() => setOpen(null)} />
      {labelsRx && (
        <LabelsDialog rx={labelsRx} onClose={() => setLabelsFor(null)} />
      )}
      {labelsDone && (
        <LabelsDialog rx={labelsDone} onClose={() => setLabelsDone(null)} />
      )}
      <PackDeliveryDialog
        delivery={packing}
        rx={packingRx}
        busy={outsideRx.busy && !outsideRx.pending}
        onClose={closePacking}
        onPack={pack}
      />
      <OutsideRxConfirmDialog
        pending={outsideRx.pending}
        confirming={outsideRx.confirming}
        onConfirm={() => void outsideRx.confirm()}
        onCancel={outsideRx.cancel}
      />
      {safetyRx && (
        <SafetyChecksDialog
          rx={safetyRx}
          busy={actions.checks.isPending}
          scanning={actions.scan.isPending}
          onScan={(itemId, body) =>
            actions.scan.mutateAsync({
              id: safetyRx.prescriptionId,
              itemId,
              body,
            })
          }
          onClose={() => setSafetyRx(null)}
          onSave={(body) =>
            actions.checks.mutateAsync({ id: safetyRx.prescriptionId, body })
          }
          onRecorded={() => {
            setSafetyRx(null);
            done(t('dispensary.checksRecorded'));
          }}
          onError={fail}
        />
      )}
      <Dialog
        open={!!oos}
        onClose={() => setOos(null)}
        title="Mark out of stock"
        footer={
          <>
            <Button onClick={() => setOos(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={actions.outOfStock.isPending}
              onClick={() => {
                if (!oos) return;
                void actions.outOfStock
                  .mutateAsync({ id: oos.prescriptionId, note })
                  .then(() => {
                    setOos(null);
                    done('Doctor notified');
                  })
                  .catch(fail);
              }}
            >
              Notify doctor
            </Button>
          </>
        }
      >
        <Field label="Reason and next step">
          <textarea
            className={styles.notes}
            rows={4}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </Dialog>
    </PageContainer>
  );
}

function PrescriptionDrawer({
  rx,
  onClose,
}: {
  rx: DispensingRx | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!rx} onClose={onClose} title={rx?.number ?? 'Prescription'}>
      {rx && (
        <div className={styles.drawer}>
          <dl className={styles.rows}>
            <dt>Patient</dt>
            <dd>{rx.patient.fullName}</dd>
            <dt>Signed by</dt>
            <dd>{rx.prescriber.fullName}</dd>
            <dt>Safety checks</dt>
            <dd>
              <RecordedChecks rx={rx} />
            </dd>
            {rx.refill && (
              <>
                <dt>Refill</dt>
                <dd>
                  <RefillDetail refill={rx.refill} />
                </dd>
              </>
            )}
            <dt>Status</dt>
            <dd>
              <Badge tone={statusTone(rx.status)}>
                {rxStatusLabel(rx.status)}
              </Badge>
            </dd>
            <dt>Allergies</dt>
            <dd>
              <DispensaryAllergies patient={rx.patient} />
            </dd>
            <dt>Outside prescription</dt>
            <dd>
              <OutsideRxDetail doc={rx.outsidePrescription} />
            </dd>
            <dt>Delivery</dt>
            <dd>
              {rx.deliverable
                ? (rx.delivery?.slot ?? 'Allowed')
                : 'Blocked for controlled/fridge items'}
            </dd>
          </dl>
          <Card title="Items" flush>
            <DataTable
              caption="Prescription items"
              rows={rx.items}
              rowKey={(item) => item.id}
              columns={[
                {
                  key: 'medicine',
                  header: 'Medicine',
                  render: (i) => i.medicine,
                },
                {
                  key: 'dose',
                  header: 'Dose',
                  render: (i) =>
                    [
                      i.dose,
                      i.frequency,
                      i.durationDays && `${i.durationDays} days`,
                    ]
                      .filter(Boolean)
                      .join(' · '),
                },
                {
                  key: 'qty',
                  header: 'Qty',
                  align: 'right',
                  render: (i) => `${i.quantity} ${i.unit}`,
                },
                {
                  key: 'stock',
                  header: 'Stock',
                  align: 'right',
                  render: (i) => i.inStock,
                },
                {
                  key: 'flags',
                  header: 'Safety',
                  render: (i) => (
                    <div className={styles.badges}>
                      {i.catalogue?.controlled && (
                        <Badge tone="warning">Controlled</Badge>
                      )}
                      {i.catalogue?.psychotropic && (
                        <Badge tone="warning">Psychotropic</Badge>
                      )}
                      {i.catalogue?.highAlert && (
                        <Badge tone="danger">High-alert</Badge>
                      )}
                      {i.catalogue?.fridge && <Badge tone="info">Fridge</Badge>}
                      {i.scanned && <Badge tone="success">Scanned</Badge>}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
          <p className={styles.sub}>
            Scan checks, FEFO warnings and stock deduction are enforced by the
            API. Delivery is blocked for controlled, psychotropic and fridge
            medicines.
          </p>
        </div>
      )}
    </Dialog>
  );
}

/** RL-1638: the original prescription and the approved request of a refill. */
function RefillDetail({ refill }: { refill: DispensingRefill }) {
  const { t } = useTranslation();
  return (
    <span>
      <Badge tone="info">{t('dispensary.refillBadge')}</Badge>{' '}
      {t('dispensary.refillOf', {
        number: refill.originalPrescription.number ?? '-',
      })}
      <br />
      <span className={styles.sub}>
        {t('dispensary.refillRequest', {
          id: refill.requestId.slice(0, 8),
          when: when(refill.approvedAt),
        })}
      </span>
    </span>
  );
}

/** RL-1643: the safety checks as the API recorded them. */
function RecordedChecks({ rx }: { rx: DispensingRx }) {
  const { t } = useTranslation();
  const by = (name: string | null | undefined, at: string | null | undefined) =>
    [name, at ? when(at) : null].filter(Boolean).join(' · ');
  return (
    <ul className={styles.recordedChecks}>
      <li>
        {t('dispensary.clinicalVerified')}:{' '}
        {rx.clinicalVerified
          ? by(
              rx.clinicalVerification?.by?.fullName,
              rx.clinicalVerification?.at,
            ) || t('dispensary.yes')
          : t('dispensary.no')}
      </li>
      <li>
        {t('dispensary.counsellingDone')}:{' '}
        {rx.counsellingDone ? t('dispensary.yes') : t('dispensary.no')}
      </li>
      {rx.idCheck && (
        <li>
          {t('dispensary.idCheckTitle')}:{' '}
          {t(
            rx.idCheck.collector === 'patient'
              ? 'dispensary.idCheckedPatient'
              : 'dispensary.idCheckedOther',
            { name: rx.idCheck.collectorName, last4: rx.idCheck.idLast4 },
          )}
        </li>
      )}
      {rx.secondChecker && (
        <li>
          {t('dispensary.secondChecker')}:{' '}
          {by(rx.secondChecker.fullName, rx.secondChecker.recordedAt)}
        </li>
      )}
      {rx.dispensedAt && (
        <li>
          {t('dispensary.dispensedAt')}: {when(rx.dispensedAt)}
        </li>
      )}
    </ul>
  );
}
